import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { SortDescriptor } from 'react-aria-components'
import { formatConversationDateTime } from '@/lib/dateFormat'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { TagList, LoadingSpinner, EmptyState } from '@/components/common'
import { useConversations, conversationKeys } from '@/hooks/api/useConversations'
import { useAssistantsByIds, useAssistantsSimple } from '@/hooks/api/useAssistants'
import { useIsMobile } from '@/hooks/use-breakpoint'
import { useAutoRefresh } from '@/hooks/useAutoRefresh'
import {
  ChannelIcons,
  OutcomeBadge,
  DurationDisplay,
  ConversationFilters,
  ConversationDrawer,
  QALabelBadge,
} from './components'
import { usePermissions } from '@/hooks/api'
import { ConversationListItem } from './components/ConversationListItem'
import { cx } from '@/lib/utils/cx'
import { transformTags } from '@/lib/tags'
import type { Conversation, ConversationListFilters } from '@/types/conversation'
import { useFilterStore } from '@/stores/filterStore'

const PERSIST_KEY = 'conversations'

// Helper to track viewed conversations in session
const getViewedConversations = (): Record<string, string> => {
  const viewed = sessionStorage.getItem('viewedConversations')
  return viewed ? JSON.parse(viewed) : {}
}

const markConversationAsViewed = (id: string | number) => {
  const viewed = getViewedConversations()
  viewed[String(id)] = new Date().toISOString()
  sessionStorage.setItem('viewedConversations', JSON.stringify(viewed))
}

const isConversationViewed = (id: string | number): boolean => {
  const viewed = getViewedConversations()
  return !!viewed[String(id)]
}

// Helper to check if conversation is from failover/non-production environment
const isFailoverConversation = (meta: string | null | undefined): boolean => {
  if (!meta) return false
  try {
    const parsed = JSON.parse(meta)
    // rootPath must exist AND not be the production API path
    return parsed.rootPath != null && parsed.rootPath !== '/api/v1'
  } catch {
    return false
  }
}

export function ConversationsPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { id: selectedConversationId, tab: currentTab } = useParams<{ id: string; tab: string }>()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { isAdmin } = usePermissions()
  const isRestoringRef = useRef(false)

  // Use Zustand store for persistence
  const { getFilters, setFilters, clearFilters: clearStoredFilters } = useFilterStore()

  // Auto-refresh conversations when tab becomes visible or drawer closes
  const refreshConversations = useAutoRefresh({
    queryKey: conversationKeys.lists(),
    refreshOnFocus: true,
  })

  // Check if URL has filter params
  const hasUrlFilters = searchParams.has('filter')

  // Restore persisted filters to URL when navigating to page without filters
  useEffect(() => {
    if (isRestoringRef.current || hasUrlFilters) return

    const persisted = getFilters(PERSIST_KEY) as ConversationListFilters | null
    if (persisted && Object.keys(persisted).length > 0) {
      isRestoringRef.current = true
      setSearchParams((current) => {
        const params = new URLSearchParams(current)
        params.set('filter', JSON.stringify(persisted))
        return params
      }, { replace: true })
      setTimeout(() => {
        isRestoringRef.current = false
      }, 0)
    }
  }, [hasUrlFilters, setSearchParams, getFilters])

  // Parse filters from URL
  const filters = useMemo((): ConversationListFilters => {
    const filterParam = searchParams.get('filter')
    if (filterParam) {
      try {
        return JSON.parse(filterParam)
      } catch {
        return {}
      }
    }
    return {}
  }, [searchParams])

  // Persist filters to store when they change
  useEffect(() => {
    setFilters(PERSIST_KEY, filters as Record<string, unknown>)
  }, [filters, setFilters])

  // Clear handler for filter component
  const handleClearFilters = useCallback(() => {
    clearStoredFilters(PERSIST_KEY)
  }, [clearStoredFilters])

  // Pagination state
  const page = parseInt(searchParams.get('page') || '1', 10)
  const perPage = parseInt(searchParams.get('perPage') || '25', 10)

  // Sort state
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'updated_at',
    direction: 'descending',
  })

  // Resolve booking_system filter to assistant IDs (frontend-only, no backend change needed)
  const hasBookingSystemFilter = (filters.booking_system?.length ?? 0) > 0
  const { data: allAssistants = [] } = useAssistantsSimple(hasBookingSystemFilter)

  const resolvedFilters = useMemo((): ConversationListFilters => {
    if (!hasBookingSystemFilter) return filters

    const bookingSystems = new Set(filters.booking_system)
    const matchingIds = allAssistants
      .filter((a) => {
        const tools = (a.config as Record<string, unknown>)?.tools
        if (!Array.isArray(tools)) return false
        return tools.some(
          (t: Record<string, unknown>) =>
            typeof t.name === 'string' && bookingSystems.has(t.name)
        )
      })
      .map((a) => a.id)

    const { booking_system: _, ...rest } = filters

    let finalIds = matchingIds
    if (rest.assistant_id?.length) {
      finalIds = matchingIds.filter((id) => rest.assistant_id!.includes(id))
    }

    return { ...rest, assistant_id: finalIds.length ? finalIds : [-1] }
  }, [filters, hasBookingSystemFilter, allAssistants])

  // Fetch conversations
  const { data, isLoading, isFetching } = useConversations({
    filters: resolvedFilters,
    page,
    perPage,
    sort: {
      field: sortDescriptor.column as string,
      order: sortDescriptor.direction === 'ascending' ? 'ASC' : 'DESC',
    },
  })

  const conversations = useMemo<Conversation[]>(() => data?.data || [], [data?.data])

  // Fetch only the assistants referenced by the current page of conversations
  const uniqueAssistantIds = useMemo(() => {
    const ids = conversations.map((c) => c.assistant_id).filter(Boolean) as number[]
    return [...new Set(ids)]
  }, [conversations])

  const { data: assistants = [] } = useAssistantsByIds(uniqueAssistantIds)
  const totalCount = data?.total || 0
  const totalPages = Math.ceil(totalCount / perPage)

  // Get assistant name by ID
  const getAssistantName = useCallback(
    (assistantId?: number) => {
      if (!assistantId) return t('resources.conversations.unknown')
      const assistant = assistants.find((a) => a.id === assistantId)
      return assistant?.name || t('resources.conversations.unknown')
    },
    [assistants, t]
  )

  // Get assistant timezone by ID
  const getAssistantTimezone = useCallback(
    (assistantId?: number): string | undefined => {
      if (!assistantId) return undefined
      const assistant = assistants.find((a) => a.id === assistantId)
      return assistant?.timezone
    },
    [assistants]
  )

  // Handle filter change
  const handleFiltersChange = useCallback(
    (newFilters: ConversationListFilters) => {
      const newParams = new URLSearchParams(searchParams)
      if (Object.keys(newFilters).length > 0) {
        newParams.set('filter', JSON.stringify(newFilters))
      } else {
        newParams.delete('filter')
      }
      newParams.set('page', '1') // Reset to first page
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('page', String(newPage))
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (newPerPage: number) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('perPage', String(newPerPage))
      newParams.set('page', '1')
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Handle row click
  const handleRowClick = useCallback(
    (conversation: Conversation) => {
      markConversationAsViewed(conversation.id)
      // Preserve existing query params (filters, pagination) when navigating
      const queryString = searchParams.toString()
      navigate(`/conversations/${conversation.id}/overview${queryString ? `?${queryString}` : ''}`)
    },
    [navigate, searchParams]
  )

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    // Navigate back to conversations list, preserving query params
    const queryString = searchParams.toString()
    navigate(`/conversations${queryString ? `?${queryString}` : ''}`)
    // Note: refresh is handled by the effect that tracks drawer close
  }, [navigate, searchParams])

  // Handle sort change
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  // Track previous drawer state to detect when it closes (via any means: back button, navigation, etc.)
  const prevSelectedIdRef = useRef<string | null>(null)
  useEffect(() => {
    // Drawer was open and is now closed -> refresh the list
    if (prevSelectedIdRef.current && !selectedConversationId) {
      refreshConversations()
    }
    prevSelectedIdRef.current = selectedConversationId
  }, [selectedConversationId, refreshConversations])

  // Keyboard navigation (left/right arrows for conversation navigation)
  useEffect(() => {
    if (!selectedConversationId || !conversations.length) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const currentIndex = conversations.findIndex(
          (c) => String(c.id) === selectedConversationId
        )
        if (currentIndex === -1) return

        const newIndex =
          e.key === 'ArrowLeft'
            ? Math.max(0, currentIndex - 1)
            : Math.min(conversations.length - 1, currentIndex + 1)

        if (newIndex !== currentIndex) {
          const newConversation = conversations[newIndex]
          markConversationAsViewed(newConversation.id)
          // Preserve query params and current tab when navigating between conversations
          const queryString = searchParams.toString()
          const tab = currentTab || 'overview'
          navigate(`/conversations/${newConversation.id}/${tab}${queryString ? `?${queryString}` : ''}`, { replace: true })
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedConversationId, conversations, searchParams, navigate, currentTab])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Empty state
  if (conversations.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {/* Filter bar - always show so user can clear/change filters */}
        <div className="flex items-center justify-end border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-900">
          <ConversationFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClear={handleClearFilters}
          />
        </div>
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState
            title={t('resources.conversations.no_conversations')}
            description={t('resources.conversations.no_conversations_hint')}
          />
        </div>
      </div>
    )
  }

  // Mobile: Card-based list
  if (isMobile) {
    return (
      <div className="flex h-full flex-col">
        {/* Mobile filter bar */}
        <div className="flex items-center justify-end border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-900">
          <ConversationFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClear={handleClearFilters}
          />
        </div>

        {/* Loading bar */}
        {isFetching && (
          <div className="h-1 w-full overflow-hidden bg-gray-100">
            <div className="h-full w-1/3 animate-pulse bg-brand-500" />
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {conversations.map((conversation, index) => (
            <ConversationListItem
              key={conversation.id}
              conversation={conversation}
              assistantName={getAssistantName(conversation.assistant_id)}
              assistantTimezone={getAssistantTimezone(conversation.assistant_id)}
              onClick={() => handleRowClick(conversation)}
              isViewed={isConversationViewed(conversation.id)}
              isAlternate={index % 2 === 1}
              isFailover={isAdmin && isFailoverConversation(conversation.meta)}
            />
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationTable
            page={page}
            total={totalPages}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePageSizeChange}
          />
        )}

        <ConversationDrawer
          conversationId={selectedConversationId}
          onClose={handleDrawerClose}
        />
      </div>
    )
  }

  // Desktop: Table card with integrated header
  return (
    <TableCard.Root size="sm" className="flex h-full flex-col">
      <TableCard.Header
        title={t('resources.conversations.overview')}
        description={t('resources.conversations.subtitle')}
        contentTrailing={
          <ConversationFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClear={handleClearFilters}
          />
        }
      />

      {/* Loading bar */}
      {isFetching && (
        <div className="h-1 w-full overflow-hidden bg-gray-100">
          <div className="h-full w-1/3 animate-pulse bg-brand-500" />
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <Table
              aria-label={t('resources.conversations.overview')}
              sortDescriptor={sortDescriptor}
              onSortChange={handleSortChange}
            >
              <Table.Header>
                <Table.Head id="updated_at" label={t('resources.conversations.fields.date')} allowsSorting isRowHeader />
                <Table.Head id="from_id" label={t('resources.conversations.fields.contact')} />
                <Table.Head id="channel" label={t('resources.conversations.fields.channels')} className="text-center" />
                <Table.Head id="topic" label={t('resources.conversations.fields.topic')} />
                <Table.Head id="summary" label={t('resources.conversations.fields.summary')} />
                <Table.Head id="outcome" label={t('resources.conversations.fields.outcome')} />
                <Table.Head id="tags" label={t('resources.conversations.fields.tags')} />
                <Table.Head id="assistant_id" label={t('resources.conversations.fields.assistant')} />
              </Table.Header>
              <AriaTableBody>
                {conversations.map((conversation) => {
                  const isViewed = isConversationViewed(conversation.id)
                  const isFailover = isAdmin && isFailoverConversation(conversation.meta)
                  return (
                    <Table.Row
                      key={conversation.id}
                      id={conversation.id}
                      className={cx(
                        'cursor-pointer',
                        isViewed && 'opacity-70 bg-gray-50 dark:bg-gray-800',
                        isFailover && 'border-l-4 border-l-amber-500'
                      )}
                      onAction={() => handleRowClick(conversation)}
                    >
                      <Table.Cell>
                        <div>
                          <div className="text-sm text-primary">
                            {conversation.updated_at
                              ? formatConversationDateTime(
                                  conversation.updated_at,
                                  getAssistantTimezone(conversation.assistant_id)
                                )
                              : t('resources.conversations.unknown')}
                          </div>
                          <div className="mt-0.5 text-xs text-tertiary">
                            <DurationDisplay seconds={conversation.duration} />
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div>
                          <div className="text-sm text-primary">
                            {conversation.from_id || t('resources.conversations.unknown')}
                          </div>
                          <div className="mt-0.5 text-xs text-tertiary">
                            {conversation.from_name || t('resources.conversations.unknown')}
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        <ChannelIcons meta={conversation.meta} twilioId={conversation.twilio_id} />
                      </Table.Cell>
                      <Table.Cell>
                        <span className="line-clamp-1 text-sm text-primary">
                          {conversation.topic || '-'}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-sm text-tertiary">
                          {conversation.summary || '-'}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-1.5">
                          {conversation.outcome ? (
                            <OutcomeBadge outcome={conversation.outcome} />
                          ) : (
                            '-'
                          )}
                          {isAdmin && <QALabelBadge meta={conversation.meta} />}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {conversation.tags && conversation.tags.length > 0 ? (
                          <TagList
                            tags={transformTags(conversation.tags)}
                            variant="colored"
                            size="sm"
                            iconOnly
                          />
                        ) : (
                          '-'
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-sm text-primary">
                          {getAssistantName(conversation.assistant_id)}
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </AriaTableBody>
            </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTable
          page={page}
          total={totalPages}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePageSizeChange}
        />
      )}

      {/* Conversation Drawer */}
      <ConversationDrawer
        conversationId={selectedConversationId}
        onClose={handleDrawerClose}
      />
    </TableCard.Root>
  )
}

export default ConversationsPage
